<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidLock';

import { ref, onMounted, provide, computed, useTemplateRef } from 'vue';

import { debounce } from 'lodash/function';

import AddressStep from '@cta/donate_call_component/components/AddressStep.vue';
import AmountStep from '@cta/donate_call_component/components/AmountStep.vue';
import PaymentStep from '@cta/donate_call_component/components/PaymentStep.vue';
import PrefillBankaccountOverlay from '@cta/donate_call_component/components/PrefillBankaccountOverlay.vue';
import TlsNotice from '@cta/donate_call_component/components/TlsNotice.vue';
import csrfToken from '@cta/shared/csrf';
import FriendlyCaptcha from '@cta/frontend/components/FriendlyCaptcha.vue';
import { localizeAmount } from '@cta/frontend/javascripts/money';
import PaymentIntervalStep from './components/PaymentIntervalStep.vue';
import ResultingAmountOverlay from './components/ResultingAmountOverlay.vue';
import initializeStore from './utils/store';
import scrollIntoView from '@cta/frontend/javascripts/scroll_into_view';
import browserService from '@cta/frontend/services/BrowserService';
import trackingService from '@cta/frontend/services/TrackingService';

const props = defineProps({
  amountChoices: {
    type: Array,
    default() {
      return [];
    },
  },
  amountPrefill: {
    type: String,
    default: undefined,
  },
  salutations: {
    type: Array,
    default() {
      return [];
    },
  },
  defaultSalutation: {
    type: String,
    default: undefined,
  },
  countryCodes: {
    type: Object,
    default() {
      return {};
    },
  },
  defaultCountryCode: {
    type: String,
    default: undefined,
  },
  regions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentTypes: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        salutation: undefined,
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        locality: undefined,
        street: undefined,
        emailaddress: undefined,
        paymentType: undefined,
        owner: undefined,
        iban: undefined,
      };
    },
  },
  validatePath: {
    type: String,
    default: undefined,
  },
  donatePath: {
    type: String,
    default: undefined,
  },
  prefillBankaccountFormCheckboxEnabled: {
    type: Boolean,
    default: false,
  },
  prefillBankaccountFormCheckboxText: {
    type: String,
    default: '',
  },
  prefillBankaccountOverlayUnlocked: {
    type: Boolean,
    default: false,
  },
  prefillBankaccountAllowPath: {
    type: String,
    default: undefined,
  },
  prefillBankaccountForbidPath: {
    type: String,
    default: undefined,
  },
  logoutPath: {
    type: String,
    default: undefined,
  },
  utmParameters: {
    type: Object,
    default: undefined,
  },
  exitUrl: {
    type: String,
    default: undefined,
  },
  defaultPaymentInterval: {
    type: String,
    default: undefined,
  },
  confirmPaymentInterval: {
    type: Boolean,
    default: false,
  },
  paymentIntervalOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentIntervalDescriptions: {
    type: Array,
    default() {
      return [];
    },
  },
  paymentIntervalUi: {
    type: String,
    default: 'select',
  },
  resultingAmountOverlayUnlocked: {
    type: Boolean,
    default: false,
  },
  resultingAmountOverlayThreshold: {
    type: Number,
    default: undefined,
  },
  prefillBankaccountConsentText: {
    type: String,
    default: '',
  },
  friendlyCaptchaSettings: {
    type: Object,
    default() {
      return {
        showCaptcha: false,
        simulateCaptchaMode: 'off',
        apiEndpoint: undefined,
        sitekey: undefined,
      };
    },
  },
});

const store = initializeStore({
  amountPrefill: props.amountPrefill,
  defaultCountryCode: props.defaultCountryCode,
  defaultPaymentInterval: props.defaultPaymentInterval,
  requirePaymentIntervalConfirmation: props.confirmPaymentInterval,
  defaultSalutation: props.defaultSalutation,
  paymentTypes: props.paymentTypes,
  prefilledAttributes: props.prefilledAttributes,
});
provide('store', store);

const form = useTemplateRef('form');
const amountStep = useTemplateRef('amount-step');
const paymentIntervalStep = useTemplateRef('payment-interval-step');
const addressStep = useTemplateRef('address-step');
const paymentStep = useTemplateRef('payment-step');

function openAmountStep() {
  store.openStep = 'amount';
  scrollIntoView(amountStep.value.$el);
}

function openPaymentIntervalStep() {
  store.openStep = 'payment-interval';
  scrollIntoView(paymentIntervalStep.value.$el);
}

function openAddressStep() {
  store.openStep = 'address';
  scrollIntoView(addressStep.value.$el);
}

function openPaymentStep() {
  store.openStep = 'payment';
  scrollIntoView(paymentStep.value.$el);
}

function closeAllSteps() {
  store.openStep = undefined;
}

function allStepsClosed() {
  return store.openStep === undefined;
}

function openFirstInvalidStep() {
  if (!store.amountStepValid) {
    openAmountStep();
  } else if (!store.paymentIntervalStepValid) {
    openPaymentIntervalStep();
  } else if (!store.addressStepValid) {
    openAddressStep();
  } else if (!store.paymentStepValid) {
    openPaymentStep();
  } else {
    closeAllSteps();
  }
}

async function fetchValidation() {
  const formData = new FormData();
  formData.append('donation[amount]', store.values.amount === undefined ? '' : store.values.amount);
  formData.append('donation[payment_interval]', store.values.paymentInterval || '');
  formData.append('donation[salutation]', store.values.salutation || '');
  formData.append('donation[firstname]', store.values.firstname || '');
  formData.append('donation[lastname]', store.values.lastname || '');
  formData.append('donation[countrycode]', store.values.countrycode || '');
  formData.append('donation[postalcode]', store.values.postalcode || '');
  formData.append('donation[region]', store.values.region || '');
  formData.append('donation[locality]', store.values.locality || '');
  formData.append('donation[street]', store.values.street || '');
  formData.append('donation[emailaddress]', store.values.emailaddress || '');
  formData.append('donation[payment_type]', store.values.paymentType || '');
  formData.append('donation[owner]', store.values.owner || '');
  formData.append('donation[iban]', store.values.rawIban);

  const response = await fetch(props.validatePath, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });
  const json = await response.json();

  return { ok: response?.ok, messages: new Map(Object.entries(json)) };
}

async function validate() {
  const { ok, messages } = await fetchValidation();
  if (ok) {
    store.errors = {
      amount: messages.get('amount'),
      firstname: messages.get('firstname'),
      lastname: messages.get('lastname'),
      street: messages.get('street'),
      postalcode: messages.get('postalcode'),
      region: messages.get('region'),
      locality: messages.get('locality'),
      emailaddress: messages.get('emailaddress'),
      paymentInterval: messages.get('payment_interval'),
      paymentType: messages.get('payment_type'),
      owner: messages.get('owner'),
      iban: messages.get('iban'),
    };
  } else {
    window.location.replace(window.location);
  }
}

const throttledValidate = debounce(async () => validate(), 500, { leading: true });

async function isAmountStepValid() {
  await validate();
  return store.amountStepValid;
}

async function isPaymentIntervalStepValid() {
  await validate();
  return store.paymentIntervalStepValid;
}

async function isAddressStepValid() {
  await validate();
  return store.addressStepValid;
}

async function isPaymentStepValid() {
  await validate();
  return store.paymentStepValid;
}

const resultingAmountModalVisible = ref(false);

function showResultingAmountModal() {
  if (props.resultingAmountOverlayUnlocked && store.values.amount >= props.resultingAmountOverlayThreshold) {
    resultingAmountModalVisible.value = true;
  }
}

async function tryOpenAmountStep() {
  if (allStepsClosed()) {
    openAmountStep();
  } else if (store.paymentIntervalStepOpen) {
    store.paymentIntervalStepConfirmationRequired = false;
    openAmountStep();
  } else {
    await validate();
    openAmountStep();
  }
}

async function commitAmount(navigate = openFirstInvalidStep) {
  if (await isAmountStepValid()) {
    showResultingAmountModal();
    trackingService.dispatchSendForm(amountStep.value.$el, {
      context: 'amount-step',
      value: store.values.amount,
    });

    navigate();
  } else {
    store.amountErrorUnlocked = true;
  }
}

async function tryOpenPaymentIntervalStep() {
  if (allStepsClosed()) {
    openPaymentIntervalStep();
  } else if (store.amountStepOpen) {
    await commitAmount(openPaymentIntervalStep);
  } else {
    if (store.paymentStepOpen || store.addressStepOpen) await validate();
    openPaymentIntervalStep();
  }
}

async function commitPaymentInterval(navigate = openFirstInvalidStep) {
  store.paymentIntervalStepConfirmationRequired = false;
  if (await isPaymentIntervalStepValid()) {
    trackingService.dispatchSendForm(paymentIntervalStep.value.$el, {
      context: 'payment-interval-step',
      value: store.values.paymentInterval,
    });

    navigate();
  } else {
    store.paymentIntervalErrorUnlocked = true;
  }
}

async function tryOpenAddressStep() {
  if (allStepsClosed()) {
    openAddressStep();
  } else if (store.amountStepOpen) {
    await commitAmount(openAddressStep);
  } else if (store.paymentIntervalStepOpen) {
    store.paymentIntervalStepConfirmationRequired = false;
    await commitPaymentInterval(openAddressStep);
  } else {
    if (store.paymentStepOpen) await validate();
    openAddressStep();
  }
}

async function commitAddress(navigate = openFirstInvalidStep) {
  if (await isAddressStepValid()) {
    trackingService.dispatchSendForm(addressStep.value.$el, {
      context: 'address-step',
    });

    navigate();
  } else {
    store.addressErrorUnlocked = true;
  }
}

async function tryOpenPaymentStep() {
  if (allStepsClosed()) {
    openPaymentStep();
  } else if (store.amountStepOpen) {
    await commitAmount(openPaymentStep);
  } else if (store.paymentIntervalStepOpen) {
    store.paymentIntervalStepConfirmationRequired = false;
    await commitPaymentInterval(openPaymentStep);
  } else if (store.addressStepOpen) {
    await commitAddress(openPaymentStep);
  } else {
    openPaymentStep();
  }
}

async function commitPayment(navigate = openFirstInvalidStep) {
  if (await isPaymentStepValid()) {
    trackingService.dispatchSendForm(paymentStep.value.$el, {
      context: 'payment-step',
    });

    navigate();
  } else {
    store.paymentErrorUnlocked = true;
  }
}

const prefillBankaccountModalVisible = ref(false);

var submitButtonActive = false;
const submitPending = ref(false);
const captchaError = ref(undefined);
const captchaSolution = ref(!props.friendlyCaptchaSettings.showCaptcha
  || props.friendlyCaptchaSettings.simulateCaptchaMode === 'auto'
  ? 'simulated-response'
  : undefined);
const captchaValid = computed(() => captchaError.value === undefined);

function captchaComplete() {
  captchaError.value = undefined;

  if (!submitButtonActive) {
    trySubmit();
  }
}

async function submit() {
  const formData = new FormData();
  formData.append('donation[amount]', store.values.amount);
  formData.append('donation[payment_interval]', store.values.paymentInterval);
  formData.append('donation[salutation]', store.values.salutation || '');
  formData.append('donation[firstname]', store.values.firstname);
  formData.append('donation[lastname]', store.values.lastname);
  formData.append('donation[street]', store.values.street || '');
  formData.append('donation[countrycode]', store.values.countrycode || '');
  formData.append('donation[postalcode]', store.values.postalcode || '');
  formData.append('donation[region]', store.values.region || '');
  formData.append('donation[locality]', store.values.locality || '');
  formData.append('donation[emailaddress]', store.values.emailaddress);
  formData.append('donation[payment_type]', store.values.paymentType);
  formData.append('donation[owner]', store.values.owner || '');
  formData.append('donation[iban]', store.values.rawIban);
  formData.append('donation[allow_prefill_bankaccount]', store.values.allowPrefillBankAccount || '');
  formData.append('donation[captcha_solution]', captchaSolution.value || '');

  Object.entries(props.utmParameters).forEach(([key, value]) => {
    if (value) formData.append(key, value);
  });

  const response = await fetch(props.donatePath, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  if (response.ok) {
    const json = await response.json();

    submitPending.value = false;

    const messages = new Map(Object.entries(json));
    captchaError.value = messages.get('captcha_solution');

    if (captchaValid.value) {
      if (props.prefillBankaccountOverlayUnlocked && store.values.paymentType === 'directdebit' && !store.values.allowPrefillBankAccount) {
        prefillBankaccountModalVisible.value = true;
      } else {
        trackingService.dispatchSendForm(form.value, {
          context: 'sponsor-form',
          value: store.values.amount,
        });
        browserService.redirectDelayed(json.redirectUrl);
      }
    }
  } else {
    window.location.replace(window.location);
  }
}

async function submitClicked() {
  submitButtonActive = true;
  submitPending.value = true;

  if (store.paymentIntervalStepOpen) {
    store.paymentIntervalStepConfirmationRequired = false;
  }

  if (store.amountStepOpen) await commitAmount();
  if (store.paymentIntervalStepOpen) await commitPaymentInterval();
  if (store.addressStepOpen) await commitAddress();
  if (store.paymentStepOpen) await commitPayment();

  trySubmit();
  submitButtonActive = false;
}

async function trySubmit() {
  if (captchaSolution.value) {
    if (store.amountStepValid && store.addressStepValid && store.paymentStepValid && store.paymentIntervalStepValid
      && submitPending.value) {
      await submit();
    }

    setTimeout(() => {
      submitPending.value = false;
    }, 1000);
  }
}

onMounted(async () => {
  if (props.amountPrefill || store.addressPrefilled || store.paymentPrefilled) {
    await validate();

    if (props.amountPrefill && store.amountStepValid) {
      showResultingAmountModal();
      openFirstInvalidStep();
    }

    if (store.addressPrefilled) {
      store.addressErrorUnlocked = !store.addressStepValid;
    }

    if (store.paymentPrefilled) {
      store.paymentErrorUnlocked = !store.paymentStepValid;
    }
  }
});
</script>

<template>
  <form
    ref="form"
    data-analytics-id="sponsor-call-form"
  >
    <AmountStep
      ref="amount-step"
      :amount-choices="amountChoices"
      analytics-id-prefix="sponsor-call"
      :step-titles="{valid: 'Betrag ausgewählt', invalid: 'Bitte wähle einen wöchentlichen Betrag'}"
      :summary="(amount) => `${localizeAmount(amount)} wöchentlich`"
      @commit="commitAmount"
      @try-open="tryOpenAmountStep"
      @validate="throttledValidate"
    />

    <PaymentIntervalStep
      ref="payment-interval-step"
      analytics-id-prefix="sponsor-call"
      :payment-interval-descriptions="paymentIntervalDescriptions"
      :payment-interval-options="paymentIntervalOptions"
      :payment-interval-ui="paymentIntervalUi"
      :show-please-select-option="!defaultPaymentInterval"
      @commit="commitPaymentInterval"
      @try-open="tryOpenPaymentIntervalStep"
    />

    <AddressStep
      ref="address-step"
      analytics-id-prefix="sponsor-call"
      :country-codes="countryCodes"
      :logout-path="logoutPath"
      :prefilled-attributes="prefilledAttributes"
      :regions="regions"
      :salutations="salutations"
      :step-number="3"
      @commit="commitAddress"
      @try-open="tryOpenAddressStep"
      @validate="throttledValidate"
    />

    <PaymentStep
      ref="payment-step"
      analytics-id-prefix="sponsor-call"
      banktransfer-label="Dauerauftrag"
      :payment-types="paymentTypes"
      :prefill-bankaccount-form-checkbox-show="prefillBankaccountFormCheckboxEnabled && prefillBankaccountOverlayUnlocked"
      :prefill-bankaccount-form-checkbox-text="prefillBankaccountFormCheckboxText"
      :prefilled-attributes="prefilledAttributes"
      :step-number="4"
      submit-button-label="Fördern"
      @commit="commitPayment"
      @try-open="tryOpenPaymentStep"
      @validate="throttledValidate"
    >
      <template #paypal-advice>
        Bitte schließe Deine Eingabe mit „Fördern“ ab.
        Du wirst umgehend auf eine PayPal-Seite geleitet.
        Dort kannst Du Deinen Förderbeitrag mit Deinen PayPal-Anmeldedaten bestätigen.
      </template>
      <template #banktransfer-advice>
        <p class="m-0">
          Bitte schließe Deine Eingabe mit „Fördern“ ab.
          Du erhältst umgehend eine E-Mail mit allen Details zur Bankverbindung.
        </p>
      </template>
    </PaymentStep>

    <FriendlyCaptcha
      v-model:solution="captchaSolution"
      :error="captchaError"
      :error-unlocked="!captchaValid"
      :settings="friendlyCaptchaSettings"
      @complete="captchaComplete"
    >
      <cmpr-button
        class="w-100 mb-50"
        data-analytics-context="sponsor-form"
        data-analytics-id="sponsor-call-form-submit-button"
        :disabled="submitPending"
        @click.prevent="submitClicked"
      >
        <cmpr-icon-solid-lock />
        Fördern
      </cmpr-button>

      <tls-notice>
        Die Übermittlung Deiner Daten erfolgt sicher via SSL-Verschlüsselung.
      </tls-notice>
    </FriendlyCaptcha>
  </form>

  <ResultingAmountOverlay
    v-if="resultingAmountOverlayUnlocked"
    :amount="store.amountStepValid ? store.values.amount : 0"
    :visible="resultingAmountModalVisible"
    @abort="openAmountStep()"
  />

  <PrefillBankaccountOverlay
    v-if="prefillBankaccountOverlayUnlocked"
    :allow-path="prefillBankaccountAllowPath"
    analytics-id-prefix="sponsor-call"
    :consent-text="prefillBankaccountConsentText"
    :forbid-path="prefillBankaccountForbidPath"
    :iban="store.values.rawIban"
    :owner="store.values.owner"
    :redirect-url="exitUrl"
    :visible="prefillBankaccountModalVisible"
  />
</template>
